import { useEffect } from "react";
import { getServiceReviewData } from "@/data/serviceReviews";

interface ServiceReviewSchemaProps {
  serviceSlug: string;
  serviceName: string;
  /** Optional location name (e.g., "Ventura") for service-location pages */
  locationName?: string;
  /** Optional location slug (e.g., "ventura") for URL construction */
  locationSlug?: string;
}

/**
 * Injects a ProfessionalService schema with embedded Review and AggregateRating
 * into the page head for Google rich results eligibility on service pages.
 * 
 * The schema follows Google's recommended structure:
 * - Reviews are embedded within the parent ProfessionalService (no itemReviewed needed)
 * - AggregateRating is embedded within the parent (no standalone schema)
 * - This avoids parent_node validation errors
 * 
 * When locationName/locationSlug are provided, the schema is tailored for
 * service-location pages (e.g., "Cabinet Painting in Ventura").
 */
export default function ServiceReviewSchema({ serviceSlug, serviceName, locationName, locationSlug }: ServiceReviewSchemaProps) {
  useEffect(() => {
    const reviewData = getServiceReviewData(serviceSlug);
    if (!reviewData) return;

    const isLocationPage = locationName && locationSlug;
    const schemaName = isLocationPage
      ? `Danis Painting Co. - ${serviceName} in ${locationName}`
      : `Danis Painting Co. - ${serviceName}`;
    const schemaUrl = isLocationPage
      ? `https://danispainting.net/service/${serviceSlug}/${locationSlug}`
      : `https://danispainting.net/service/${serviceSlug}`;

    const schema = {
      "@context": "https://schema.org",
      "@type": "HousePainter",
      "name": schemaName,
      "url": schemaUrl,
      "logo": "https://danispainting.net/logo.png",
      "telephone": "+1-805-403-8727",
      "email": "danispainting@gmail.com",
      "priceRange": "$$",
      "image": "https://d2xsxph8kpxj0f.cloudfront.net/310519663313033271/eUvaV5v4nLRJmrJ6AKc5ww/gallery-5_d36da2e9_a4c69cb6.webp",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "133 E De La Guerra St, #267",
        "addressLocality": "Santa Barbara",
        "addressRegion": "CA",
        "postalCode": "93101",
        "addressCountry": "US"
      },
      ...(isLocationPage && {
        "areaServed": {
          "@type": "City",
          "name": locationName
        }
      }),
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": reviewData.aggregateRating.ratingValue,
        "reviewCount": reviewData.aggregateRating.reviewCount,
        "bestRating": reviewData.aggregateRating.bestRating,
        "worstRating": reviewData.aggregateRating.worstRating
      },
      "review": reviewData.reviews.map(review => ({
        "@type": "Review",
        "author": {
          "@type": "Person",
          "name": review.author
        },
        "datePublished": review.date,
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": review.rating.toString(),
          "bestRating": "5",
          "worstRating": "1"
        },
        "reviewBody": review.text
      }))
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "service-review-schema";
    script.textContent = JSON.stringify(schema);

    // Remove existing if present (React re-renders)
    const existing = document.getElementById("service-review-schema");
    if (existing) {
      existing.remove();
    }

    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.getElementById("service-review-schema");
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [serviceSlug, serviceName, locationName, locationSlug]);

  return null;
}
